import type { NotificationListItem, NotificationSummary } from './apiContracts';
import type { createPrototypeApiClient } from './apiClient';
import { byId } from './domUtils';
import { type FilterKey, hideSkeleton, renderList, renderSkeleton } from './messagesPageRender';
import { showToast } from './toastController';
import { topicReadyPathFromNotification } from './notificationActionRoute';
import type { Root } from './prototypeTypes';

type Api = ReturnType<typeof createPrototypeApiClient>;
export interface MessagesAuthSnapshot { isLoggedIn: boolean; userId: string | null; }
export interface MessagesPageController { syncAuth(auth: MessagesAuthSnapshot, refresh?: boolean): Promise<void>; destroy(): void; }
interface RefreshResult { list: boolean; summary: boolean; }
const STARTUP_REFRESH_FRESHNESS_MS = 5_000;
const mountedControllers = new WeakMap<HTMLElement, MessagesPageController>();

function viewFor(root: Root): HTMLElement | null { return root.querySelector<HTMLElement>('#view-messages'); }
function authKey(auth: MessagesAuthSnapshot): string { return auth.isLoggedIn && auth.userId ? `user:${auth.userId}` : 'visitor'; }

export function getOrCreateMessagesPageController(root: Root, api: Api, authProvider?: () => MessagesAuthSnapshot): MessagesPageController {
	const view = viewFor(root);
	if (!view) return { syncAuth: async () => undefined, destroy: () => undefined };
	const messagesView = view;
	const existing = mountedControllers.get(messagesView);
	if (existing) return existing;
	const doc = messagesView.ownerDocument;
	const win = doc.defaultView;
	let items: NotificationListItem[] = [];
	let nextCursor: string | null = null;
	let lastSummary: NotificationSummary | null = null;
	let currentFilter: FilterKey = 'all';
	let expandedId: string | null = null;
	let currentAuthKey = '';
	let generation = 0;
	let listRequest = 0;
	let summaryRequest = 0;
	let loadingMore = false;
	let paginationRetry: 'append' | 'refresh' | null = null;
	let refreshInFlight: { generation: number; promise: Promise<RefreshResult> } | null = null;
	let observer: IntersectionObserver | null = null;
	let destroyed = false;
	let lastSuccessfulRefreshAt = 0;
	let lastSuccessfulRefreshGeneration = -1;

	function setHidden(id: string, hidden: boolean): void { const element = byId(root, id) as HTMLElement | null; if (element) element.hidden = hidden; }
	function setPaginationStatus(value: string, retry: 'append' | 'refresh' | null = null): void {
		const message = byId(root, 'msgPaginationMessage');
		const retryButton = byId(root, 'msgPaginationRetry') as HTMLButtonElement | null;
		if (message) message.textContent = value;
		paginationRetry = retry;
		if (retryButton) retryButton.hidden = !retry;
	}
	function setMutationError(message: string | null): void {
		const element = byId(root, 'msgMutationError');
		if (element) { element.textContent = message ?? ''; element.hidden = !message; }
	}
	function markAllButton(summary: NotificationSummary | null, busy = false): void {
		const button = byId(root, 'msgMarkAllRead') as HTMLButtonElement | null;
		if (button) button.disabled = busy || !summary || summary.unread <= 0;
	}
	function updatePills(): void {
		messagesView.querySelectorAll<HTMLButtonElement>('[data-msg-filter]').forEach((pill) => {
			const active = pill.dataset.msgFilter === currentFilter;
			pill.classList.toggle('active', active);
			pill.setAttribute('aria-pressed', String(active));
		});
	}
	function clearState(): void {
		items = []; nextCursor = null; lastSummary = null; expandedId = null; loadingMore = false; refreshInFlight = null; lastSuccessfulRefreshAt = 0; lastSuccessfulRefreshGeneration = -1;
		listRequest += 1; summaryRequest += 1; hideSkeleton(root); byId(root, 'msgList')?.replaceChildren();
		setHidden('msgEmpty', true); setHidden('msgListError', true); setHidden('msgAuthRequired', true); setMutationError(null); setHidden('msgSummaryError', true);
		setPaginationStatus(''); markAllButton(null); currentFilter = 'all'; updatePills();
	}
	function renderVisitor(): void { clearState(); setHidden('msgAuthRequired', false); }
	function params(): { status: FilterKey; cursor?: string | null } { return { status: currentFilter }; }

	function uniqueItems(entries: NotificationListItem[]): NotificationListItem[] {
		const seen = new Set<string>();
		return entries.filter((item) => !seen.has(item.id) && (seen.add(item.id), true));
	}
	function restoreToggleFocus(id: string): void {
		const toggle = [...messagesView.querySelectorAll<HTMLButtonElement>('.msg-row-toggle')].find((candidate) => candidate.dataset.notificationId === id);
		toggle?.focus({ preventScroll: true });
	}
	async function loadList(append = false, preserveVisible = false): Promise<boolean> {
		if (destroyed || currentAuthKey === 'visitor' || (append && (!nextCursor || loadingMore))) return false;
		const request = ++listRequest;
		const capturedGeneration = generation;
		const capturedFilter = currentFilter;
		if (append) { loadingMore = true; setPaginationStatus('Loading more messages…'); }
		else if (!preserveVisible) { items = []; nextCursor = null; expandedId = null; renderSkeleton(root); }
		setHidden('msgListError', true);
		try {
			const page = await api.listNotifications({ ...params(), cursor: append ? nextCursor : null });
			if (destroyed || request !== listRequest || capturedGeneration !== generation || capturedFilter !== currentFilter) return false;
			items = append ? uniqueItems([...items, ...page.items]) : uniqueItems(page.items);
			nextCursor = page.nextCursor;
			hideSkeleton(root); renderList(root, items, expandedId);
			setPaginationStatus(nextCursor ? 'More messages available.' : '');
			return true;
		} catch {
			if (destroyed || request !== listRequest || capturedGeneration !== generation || capturedFilter !== currentFilter) return false;
			hideSkeleton(root);
			if (append) setPaginationStatus('Could not load more messages.', 'append');
			else if (preserveVisible && items.length > 0) setPaginationStatus('Messages were marked read, but the latest list could not be refreshed.', 'refresh');
			else { byId(root, 'msgList')?.replaceChildren(); setHidden('msgEmpty', true); setHidden('msgListError', false); }
			return false;
		} finally {
			if (append && request === listRequest && capturedGeneration === generation && capturedFilter === currentFilter) loadingMore = false;
		}
	}
	async function loadSummary(): Promise<boolean> {
		if (destroyed || currentAuthKey === 'visitor') return false;
		const request = ++summaryRequest;
		const capturedGeneration = generation;
		try {
			const summary = await api.getNotificationSummary();
			if (destroyed || request !== summaryRequest || capturedGeneration !== generation) return false;
			lastSummary = summary; markAllButton(summary); setHidden('msgSummaryError', true); return true;
		} catch {
			if (destroyed || request !== summaryRequest || capturedGeneration !== generation) return false;
			markAllButton(lastSummary); setHidden('msgSummaryError', false); return false;
		}
	}
	async function refresh(preserveVisible = false, force = false): Promise<RefreshResult> {
		const capturedGeneration = generation;
		if (refreshInFlight?.generation === capturedGeneration) return refreshInFlight.promise;
		if (!force && lastSuccessfulRefreshGeneration === capturedGeneration && lastSuccessfulRefreshAt > 0 && Date.now() - lastSuccessfulRefreshAt < STARTUP_REFRESH_FRESHNESS_MS) return { list: true, summary: true };
		const promise = Promise.all([loadList(false, preserveVisible), loadSummary()]).then(([list, summary]) => {
			if (list && summary && capturedGeneration === generation) {
				lastSuccessfulRefreshAt = Date.now();
				lastSuccessfulRefreshGeneration = capturedGeneration;
			}
			return { list, summary };
		}).finally(() => { if (refreshInFlight?.generation === capturedGeneration) refreshInFlight = null; });
		refreshInFlight = { generation: capturedGeneration, promise };
		return promise;
	}
	async function setFilter(filter: FilterKey): Promise<void> {
		if (filter === currentFilter || currentAuthKey === 'visitor') return;
		generation += 1; currentFilter = filter; updatePills(); await refresh(); requestMoreIfSentinelVisible();
	}
	async function toggleExpand(id: string): Promise<void> {
		const item = items.find((candidate) => candidate.id === id);
		if (!item) return;
		expandedId = expandedId === id ? null : id;
		const capturedGeneration = generation;
		renderList(root, items, expandedId);
		restoreToggleFocus(id);
		if (item.readAt || expandedId !== id) return;
		setMutationError(null);
		try {
			const result = await api.markNotificationRead(id);
			if (destroyed || capturedGeneration !== generation) return;
			if (currentFilter === 'unread') { items = items.filter((candidate) => candidate.id !== id); expandedId = null; }
			else item.readAt = result.readAt;
			renderList(root, items, expandedId);
			if (currentFilter !== 'unread') restoreToggleFocus(id);
			void loadSummary();
			doc.dispatchEvent(new Event('deepmox:notifications-invalidated'));
		} catch {
			if (!destroyed && capturedGeneration === generation) setMutationError('Could not mark this message as read. Please try again.');
		}
	}
	function openTopicReady(item: NotificationListItem, path: string): void {
		if (!item.readAt) void api.markNotificationRead(item.id).catch(() => undefined);
		// Marking read is best effort and must not delay the Topic navigation.
		window.history.pushState({}, '', path);
		window.dispatchEvent(new PopStateEvent('popstate'));
	}
	async function markAllRead(): Promise<void> {
		const capturedGeneration = generation;
		const button = byId(root, 'msgMarkAllRead') as HTMLButtonElement | null;
		if (button?.disabled) return;
		markAllButton(lastSummary, true);
		setMutationError(null);
		try {
			const result = await api.markAllNotificationsRead();
			if (destroyed || capturedGeneration !== generation) return;
			if (currentFilter === 'unread') items = items.filter((item) => Boolean(item.readAt));
			else items = items.map((item) => ({ ...item, readAt: item.readAt ?? result.readAt }));
			nextCursor = null;
			lastSummary = lastSummary ? { ...lastSummary, unread: result.unreadCount } : null;
			expandedId = null;
			renderList(root, items, expandedId);
			markAllButton(lastSummary);
			doc.dispatchEvent(new Event('deepmox:notifications-invalidated'));
			const refreshed = await refresh(true, true);
			if (refreshed.list && refreshed.summary) showToast({ type: 'success', message: 'All messages marked as read.' });
			else setMutationError('Messages were marked as read, but the latest list or status could not be refreshed.');
		} catch {
			if (!destroyed && capturedGeneration === generation) { markAllButton(lastSummary); setMutationError('Could not mark all messages as read. Please try again.'); }
		}
	}
	function requestMore(): void { if (!refreshInFlight && nextCursor && !loadingMore) void loadList(true); }
	function requestMoreIfSentinelVisible(): void {
		const sentinel = byId(root, 'msgSentinel');
		if (!messagesView.classList.contains('active') || !sentinel || !win) return;
		const bounds = sentinel.getBoundingClientRect();
		if (bounds.top <= win.innerHeight + 240 && bounds.bottom >= -240) requestMore();
	}
	function setupPagination(): void {
		const sentinel = byId(root, 'msgSentinel');
		if (!sentinel) return;
		if (win && 'IntersectionObserver' in win) {
			observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) requestMore(); }, { rootMargin: '240px 0px' });
			observer.observe(sentinel);
		}
	}
	const onScroll = () => {
		if (observer || !win || !nextCursor || loadingMore) return;
		if (win.innerHeight + win.scrollY >= doc.documentElement.scrollHeight - 240) requestMore();
	};
	const onClick = (event: MouseEvent) => {
		const target = event.target as HTMLElement | null;
		if (target?.closest('[data-proto-click]')) return;
		const pill = target?.closest<HTMLButtonElement>('[data-msg-filter]');
		if (pill?.dataset.msgFilter) { void setFilter(pill.dataset.msgFilter as FilterKey); return; }
		if (target?.closest('#msgListRetry')) { void refresh(false, true); return; }
		if (target?.closest('#msgSummaryRetry')) { void loadSummary(); return; }
		if (target?.closest('#msgPaginationRetry')) {
			if (paginationRetry === 'append') void loadList(true);
			if (paginationRetry === 'refresh') void loadList(false, true);
			return;
		}
		if (target?.closest('#msgMarkAllRead')) { void markAllRead(); return; }
		const toggle = target?.closest<HTMLButtonElement>('.msg-row-toggle');
		if (toggle?.dataset.notificationId) {
			const item = items.find((candidate) => candidate.id === toggle.dataset.notificationId);
			const topicPath = item && topicReadyPathFromNotification(item.eventType, item.actionUrl);
			if (item && topicPath) openTopicReady(item, topicPath);
			else void toggleExpand(toggle.dataset.notificationId);
		}
	};
	const onAuthChange = () => { if (authProvider) void controller.syncAuth(authProvider(), messagesView.classList.contains('active')); };
	messagesView.addEventListener('click', onClick); win?.addEventListener('scroll', onScroll, { passive: true }); setupPagination();
	if (authProvider) doc.addEventListener('deepmox:auth-state-changed', onAuthChange);
	const controller: MessagesPageController = {
		async syncAuth(auth, shouldRefresh = true) {
			const nextKey = authKey(auth);
			if (nextKey !== currentAuthKey) { currentAuthKey = nextKey; generation += 1; clearState(); }
			if (nextKey === 'visitor') { renderVisitor(); return; }
			setHidden('msgAuthRequired', true);
			if (shouldRefresh) { await refresh(); requestMoreIfSentinelVisible(); }
		},
		destroy() {
			if (destroyed) return;
			destroyed = true; generation += 1; observer?.disconnect(); observer = null;
			messagesView.removeEventListener('click', onClick); win?.removeEventListener('scroll', onScroll);
			if (authProvider) doc.removeEventListener('deepmox:auth-state-changed', onAuthChange);
			mountedControllers.delete(messagesView);
		},
	};
	mountedControllers.set(messagesView, controller);
	return controller;
}

export function destroyMessagesController(root: Root): void { const view = viewFor(root); if (view) mountedControllers.get(view)?.destroy(); }
